import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, secondsToHM } from "../lib/api";
import KidQuickActionsModal from "../components/KidQuickActionsModal";
import { TimeSheet, MessageSheet } from "../components/ParentSheets";
import { StatusPill, hm, kidStatus } from "../lib/kidLook";
import { toast } from "sonner";
import AgreementCard from "../components/AgreementCard";
import { ChevronLeft, ChevronRight, Pause, Play, Plus, Trash2, Clock, Smartphone, Tablet, Laptop, Gamepad2, Save, Power, Circle, Apple, ShieldCheck, CalendarClock, PartyPopper, CheckCircle2, MonitorSmartphone, MessageCircle } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

const DEVICE_ICON = { phone: Smartphone, tablet: Tablet, laptop: Laptop, console: Gamepad2, other: Circle, ios: Apple };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

// Format "15:00" → "3:00 PM"
function fmt12h(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return hhmm || "";
  const [h, m] = hhmm.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return hhmm;
  const am = h < 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}

// Short countdown like "1h 24m", "42m", "5s"
function fmtCountdown(seconds) {
  seconds = Math.max(0, Math.round(seconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

// Human-friendly schedule status derived from the policy response.
// Uses now_local (kid's local time) so it's DST/timezone-safe.
function scheduleCountdown(policy) {
  if (!policy || !policy.window || !policy.now_local) return "";
  try {
    const now = new Date(policy.now_local);
    const [sh, sm] = policy.window.start.split(":").map(Number);
    const [eh, em] = policy.window.end.split(":").map(Number);
    const start = new Date(now); start.setHours(sh, sm, 0, 0);
    const end = new Date(now); end.setHours(eh, em, 0, 0);
    if (policy.in_window) {
      const secsLeft = (end - now) / 1000;
      return `Ends in ${fmtCountdown(secsLeft)}`;
    }
    if (now < start) {
      const secsToStart = (start - now) / 1000;
      return `Starts in ${fmtCountdown(secsToStart)} (${fmt12h(policy.window.start)})`;
    }
    return `Ended for today — resumes tomorrow`;
  } catch { return ""; }
}

export default function KidDetail() {
  const { id } = useParams();
  const kidId = id;
  const navigate = useNavigate();
  const [kid, setKid] = useState(null);
  const [devices, setDevices] = useState([]);
  const [usage, setUsage] = useState({ sessions: [], daily: [] });
  const [error, setError] = useState("");
  const [grid, setGrid] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [dragging, setDragging] = useState(null); // 'set' | 'unset' | null
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [deviceForm, setDeviceForm] = useState({ name: "", type: "phone", identifier: "" });
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", weekly_hours_pool: 20, reset_weekday: 0, pin: "" });
  const [pairing, setPairing] = useState(null); // { code, expires_at } | null
  const [siblings, setSiblings] = useState([]);
  const [iosSelection, setIosSelection] = useState({ has_selection: false });
  const [completions, setCompletions] = useState([]);
  const [timeMode, setTimeMode] = useState(null); // "give" | "take" | null
  const [messageOpen, setMessageOpen] = useState(false);
  const [tokenOpen, setTokenOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [k, d, u, all, sel, comps] = await Promise.all([
        api.get(`/kids/${id}`),
        api.get(`/kids/${id}/devices`),
        api.get(`/kids/${id}/usage?days=7`),
        api.get(`/kids`),
        api.get(`/kids/${id}/ios/selection`).catch(() => ({ data: { has_selection: false } })),
        api.get(`/chores/completions?kid_id=${id}&days=7`).catch(() => ({ data: [] })),
      ]);
      setKid(k.data);
      setDevices(d.data);
      setUsage(u.data);
      setSiblings(all.data.filter((x) => x.id !== id));
      setIosSelection(sel.data || { has_selection: false });
      setCompletions(comps.data || []);
      if (!dirty) setGrid(k.data.schedule);
      setEditForm({
        name: k.data.name,
        weekly_hours_pool: k.data.weekly_hours_pool,
        reset_weekday: k.data.reset_weekday,
        pin: "",
        username: k.data.username || "",
      });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [id, dirty]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // 1-second tick so the schedule countdown label stays fresh between fetches
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const togglePause = async () => {
    try {
      await api.post(`/kids/${id}/${kid.paused ? "resume" : "pause"}`);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const startSession = async () => {
    try {
      await api.post(`/kids/${id}/start-session`);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const stopSession = async () => {
    try {
      await api.post(`/kids/${id}/stop-session`);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const setCell = (day, hour, value) => {
    setGrid((g) => {
      if (!g) return g;
      const newGrid = g.map((row) => [...row]);
      newGrid[day][hour] = value;
      return newGrid;
    });
    setDirty(true);
  };

  const saveSchedule = async () => {
    try {
      await api.put(`/kids/${id}/schedule`, { grid });
      setDirty(false);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const addDevice = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/kids/${id}/devices`, deviceForm);
      setShowAddDevice(false);
      setDeviceForm({ name: "", type: "phone", identifier: "" });
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeDevice = async (did) => {
    try {
      await api.delete(`/devices/${did}`);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeKid = async () => {
    if (!window.confirm("Remove this kid and all their data?")) return;
    try {
      await api.delete(`/kids/${id}`);
      navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveKidEdits = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editForm };
      if (!payload.pin) delete payload.pin;
      if (!payload.username) delete payload.username;
      // NEVER send weekly_hours_pool from the Edit modal — that would trigger a
      // backend respread of daily_hours = weekly/7 and wipe the parent's per-day
      // schedule tweaks. Hours are managed in the Daily Schedule editor below.
      delete payload.weekly_hours_pool;
      await api.patch(`/kids/${id}`, payload);
      setEditing(false);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const startIOSPairing = async () => {
    try {
      const { data } = await api.post(`/kids/${id}/ios/pair-init`);
      setPairing({ ...data, kind: "ios" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const startWindowsPairing = async () => {
    try {
      const { data } = await api.post(`/kids/${id}/windows/pair-init`);
      setPairing({ ...data, kind: "windows" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const stats = useMemo(() => {
    if (!kid) return null;
    const pol = kid.policy || {};
    const allowance = pol.allowance_seconds ?? 0;
    const bank = pol.bank_seconds ?? 0;
    const total = pol.total_today_seconds ?? (allowance + bank);
    const used = pol.used_today_seconds ?? 0;
    const remaining = pol.remaining_today_seconds ?? Math.max(0, total - used);
    const percent = total > 0 ? (remaining / total) * 100 : 0;
    return { pool: total, allowance, bank, used, remaining, percent };
  }, [kid]);

  const chartData = useMemo(
    () => (usage.daily || []).map((d) => ({ date: d.date.slice(5), hours: +(d.seconds / 3600).toFixed(2) })),
    [usage]
  );

  if (!kid) {
    return (
      <div className="text-[#707A6E]" data-testid="kid-loading">Loading…</div>
    );
  }

  return (
    <div data-testid="kid-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-0.5 text-[16px] font-medium text-[#5C7A4F]" data-testid="back-link">
          <ChevronLeft size={20} /> Family
        </Link>
        <div className="flex items-center gap-1">
          <button data-testid="edit-kid-btn" onClick={() => setEditing(true)}
                  className="rounded-xl px-3 py-2 text-[15px] font-semibold text-[#5C7A4F] hover:bg-[#F0EFEB]">
            Edit
          </button>
          <button data-testid="delete-kid-btn" onClick={removeKid}
                  className="rounded-xl px-3 py-2 text-[15px] font-semibold text-[#C06E52] hover:bg-[#F6E3DC]">
            <Trash2 size={15} className="inline mr-1 -mt-0.5" /> Remove
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="kid-error" className="mt-4 text-sm text-[#C06E52] bg-[#F6E3DC] rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
        {/* Time ring, name and status, as in the app */}
        <div className="card-soft flex items-center gap-4 p-[18px]" data-testid="kid-status-card">
          <TimeRing left={stats.remaining} total={stats.pool} colour={kidStatus(kid).bar} />
          <div className="min-w-0">
            <h1 className="truncate text-[26px] font-bold tracking-tight text-[#2C342A]" data-testid="kid-name-heading">{kid.name}</h1>
            <div className="my-1.5" data-testid="kid-status-label"><StatusPill status={kidStatus(kid)} /></div>
            <div className="text-[13px] leading-relaxed text-[#707A6E]">
              Used {hm(stats.used)} of {hm(stats.pool)}
              {stats.bank > 0 && <> · <span className="text-[#5C7A4F]">+{hm(stats.bank)} saved up</span></>}
              <br />
              <span data-testid="schedule-status">
                {kid.policy?.window
                  ? <>Allowed {fmt12h(kid.policy.window.start)} – {fmt12h(kid.policy.window.end)}</>
                  : "No allowed hours set"}
              </span>
              {scheduleCountdown(kid.policy) && (
                <>{" · "}<span className="text-[#5C7A4F]" data-testid="schedule-countdown">{scheduleCountdown(kid.policy)}</span></>
              )}
              {devices.length > 0 && <><br />{devices.map((d) => d.name).join(" · ")}</>}
            </div>
          </div>
        </div>

        {/* Four big actions, as in the app */}
        <div>
          <div className="grid grid-cols-2 gap-2.5">
            <ActionTile testid="big-pause-btn" onClick={togglePause}
                        icon={kid.paused ? <Play size={16} fill="currentColor" /> : <Pause size={16} fill="currentColor" />}
                        tint={kid.paused ? "#E6EDE1" : "#FDF1D8"} iconColour={kid.paused ? "#5C7A4F" : "#E0A33A"}
                        title={kid.paused ? "Resume" : "Pause"}
                        detail={kid.paused ? "Lets every device back on" : "Stops every device now"} />
            <ActionTile testid="give-time-btn" onClick={() => setTimeMode("give")} icon="＋" tint="#E6EDE1" iconColour="#5C7A4F"
                        title="Give time" detail="Extra minutes, even at bedtime" />
            <ActionTile testid="take-time-btn" onClick={() => setTimeMode("take")} icon="－" tint="#F6E3DC" iconColour="#C06E52"
                        title="Take time" detail="With a reason they'll see" />
            <ActionTile testid="message-btn" onClick={() => setMessageOpen(true)} icon={<MessageCircle size={16} />}
                        tint="#E1EFF9" iconColour="#2D79B0" title="Message" detail="Pops up on their screen" />
          </div>
          <button onClick={() => setTokenOpen(true)} data-testid="give-token-btn"
                  className="card-soft mt-2.5 flex w-full items-center gap-3 p-3.5 text-left hover:bg-[#FAFAF7]">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FDF1D8] text-lg">⭐</span>
            <span className="flex-1">
              <span className="block text-[15px] font-semibold text-[#2C342A]">Give a token</span>
              <span className="block text-xs text-[#707A6E]">Or let them on for a while, or take a few minutes</span>
            </span>
            <ChevronRight size={18} className="text-[#707A6E]" />
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        {/* Usage chart */}
        <div className="card-soft p-6 md:p-8 lg:col-span-3" data-testid="usage-chart-card">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#707A6E] mb-1">Past 7 days</div>
              <h3 className="font-heading text-2xl font-medium text-[#2C342A]">Daily internet usage</h3>
            </div>
            <div className="text-sm text-[#707A6E]">
              Today: <span className="font-medium text-[#2C342A]">{secondsToHM(stats?.allowance || 0)}</span>
              {stats?.bank > 0 ? <> · <span className="text-[#5C7A4F]">+{secondsToHM(stats.bank)} bank</span></> : null}
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="usage" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5C7A4F" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#5C7A4F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#707A6E" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#707A6E" }} axisLine={false} tickLine={false} width={32} />
                <Tooltip
                  contentStyle={{ background: "white", border: "1px solid #E4E2DC", borderRadius: 12, fontSize: 12 }}
                  formatter={(v) => [`${v}h`, "Usage"]}
                />
                <Area type="monotone" dataKey="hours" stroke="#5C7A4F" strokeWidth={2.5} fill="url(#usage)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Agreement: don't delete Yoda Watch */}
        <div className="card-soft p-8 lg:col-span-3">
          <AgreementCard kidId={id} kidName={kid.name} />
        </div>

        {/* Daily allowance + windows + holidays */}
        <div className="card-soft p-8 lg:col-span-3" data-testid="daily-schedule-card">
          <DailyScheduleEditor key={id} kidId={id} kid={kid} siblings={siblings} onSaved={load} />
        </div>

        {/* Schedule */}
        <div className="card-soft p-8 lg:col-span-3" data-testid="schedule-card">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#707A6E] mb-1">Weekly schedule</div>
              <h3 className="font-heading text-2xl font-medium text-[#2C342A]">Quiet hours</h3>
              <p className="text-[#707A6E] text-sm mt-1">Click cells to block (terracotta) or allow (sand). Drag to paint multiple.</p>
            </div>
            <button
              data-testid="save-schedule-btn"
              onClick={saveSchedule}
              disabled={!dirty}
              className="inline-flex items-center gap-2 rounded-xl bg-[#5C7A4F] text-white px-4 py-2.5 font-medium hover:bg-[#4B6640] disabled:opacity-50"
            >
              <Save size={16} /> {dirty ? "Save schedule" : "Saved"}
            </button>
          </div>

          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <div className="grid" style={{ gridTemplateColumns: `48px repeat(24, minmax(18px, 1fr))` }}>
                <div></div>
                {Array.from({ length: 24 }).map((_, h) => (
                  <div key={h} className="text-[10px] text-[#707A6E] text-center pb-1">
                    {h % 3 === 0 ? h : ""}
                  </div>
                ))}
                {(grid || []).map((row, day) => (
                  <Fragment key={`d-${day}`}>
                    <div className="text-xs text-[#707A6E] flex items-center pr-2">
                      {DAYS[day]}
                    </div>
                    {row.map((blocked, hour) => (
                      <button
                        key={`${day}-${hour}`}
                        type="button"
                        data-testid={`sched-cell-${day}-${hour}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const next = !blocked;
                          setDragging(next ? "set" : "unset");
                          setCell(day, hour, next);
                        }}
                        onMouseEnter={() => {
                          if (dragging) setCell(day, hour, dragging === "set");
                        }}
                        onMouseUp={() => setDragging(null)}
                        className="sched-cell h-6 m-[1px] rounded-md"
                        style={{ background: blocked ? "#C06E52" : "#E8E6DF" }}
                        aria-label={`${DAYS[day]} ${hour}:00 ${blocked ? "blocked" : "allowed"}`}
                      />
                    ))}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Devices */}
        <div className="card-soft p-8 lg:col-span-3" data-testid="devices-card">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#707A6E] mb-1">Devices</div>
              <h3 className="font-heading text-2xl font-medium text-[#2C342A]">Tracked devices</h3>
            </div>
            <button
              data-testid="add-device-btn"
              onClick={() => setShowAddDevice(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-[#5C7A4F] text-white px-4 py-2.5 font-medium hover:bg-[#4B6640]"
            >
              <Plus size={16} /> Add device
            </button>
            <button
              data-testid="pair-ios-btn"
              onClick={startIOSPairing}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E4E2DC] bg-white px-4 py-2.5 font-medium text-[#2C342A] hover:bg-[#F0EFEB]"
            >
              <Apple size={16} /> Pair iPhone / iPad
            </button>
            <button
              data-testid="pair-windows-btn"
              onClick={startWindowsPairing}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E4E2DC] bg-white px-4 py-2.5 font-medium text-[#2C342A] hover:bg-[#F0EFEB]"
            >
              <MonitorSmartphone size={16} /> Pair Windows PC
            </button>
          </div>

          {devices.length === 0 ? (
            <p className="text-[#707A6E] text-sm">No devices added yet. Add a phone, tablet, or laptop to track.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {devices.map((d) => {
                const Icon = DEVICE_ICON[d.type] || Circle;
                return (
                  <div
                    key={d.id}
                    className="rounded-2xl border border-[#E4E2DC] bg-white p-4 flex items-center gap-3"
                    data-testid={`device-card-${d.id}`}
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8E6DF] text-[#2C342A]">
                      <Icon size={18} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#2C342A] truncate">{d.name}</div>
                      <div className="text-xs text-[#707A6E] truncate">{d.identifier || d.type}</div>
                    </div>
                    <button
                      data-testid={`remove-device-${d.id}`}
                      onClick={() => removeDevice(d.id)}
                      className="text-[#C06E52] hover:bg-[#C06E52]/10 p-2 rounded-lg"
                      aria-label="Remove device"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-6 text-xs text-[#707A6E] border-t border-[#E4E2DC] pt-4">
            Enforcement agent endpoint: <code className="bg-[#E8E6DF] rounded px-1.5 py-0.5">GET /api/agent/status?kid_id={kid.id}</code>
          </div>
        </div>

        {/* Shielded apps (read-only summary of the FamilyActivityPicker selection) */}
        <div className="card-soft p-8 lg:col-span-3" data-testid="shielded-apps-card">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div className="flex items-start gap-3">
              <span className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${iosSelection.has_selection ? "bg-[#5C7A4F]/10 text-[#5C7A4F]" : "bg-[#D99A2B]/15 text-[#D99A2B]"}`}>
                <ShieldCheck size={20} />
              </span>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#707A6E] mb-1">Apps to shield</div>
                <h3 className="font-heading text-2xl font-medium text-[#2C342A]">
                  {iosSelection.has_selection ? "Configured on iPad" : "Not configured yet"}
                </h3>
                <p className="text-[#707A6E] text-sm mt-2 max-w-xl">
                  {iosSelection.has_selection
                    ? "The parent picked which apps and categories to shield directly on the paired iPad. Apple keeps the exact app list private to that device — we only track when it was last set."
                    : "Apple requires that apps to shield are picked on the physical iPad. On the paired device, tap the \"Yoda Watch\" title 5 times → sign in with your parent password → the picker opens."}
                </p>
                {iosSelection.has_selection && iosSelection.updated_at && (
                  <p className="text-[11px] text-[#707A6E] mt-2" data-testid="shielded-apps-updated-at">
                    Last updated {new Date(iosSelection.updated_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Chores completed by this kid (last 7 days) */}
        <div className="card-soft p-8 lg:col-span-3" data-testid="chore-completions-card">
          <div className="flex items-start gap-3 mb-4">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#5C7A4F]/10 text-[#5C7A4F]">
              <CheckCircle2 size={20} />
            </span>
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#707A6E] mb-1">Chores completed</div>
              <h3 className="font-heading text-2xl font-medium text-[#2C342A]">
                {completions.length > 0 ? `${completions.length} in the last 7 days` : "No completions this week"}
              </h3>
              <p className="text-[#707A6E] text-sm mt-1">
                Kids tap chores in the iPad app to mark them done. You can remove an accidental completion here.
              </p>
            </div>
          </div>
          {completions.length > 0 && (
            <div className="mt-4 divide-y divide-[#F0EFEB]" data-testid="chore-completions-list">
              {completions.slice(0, 12).map((c) => (
                <div key={c.id} className="flex items-center justify-between py-3" data-testid={`completion-${c.id}`}>
                  <div>
                    <div className="text-[#2C342A] font-medium">{c.chore_title}</div>
                    <div className="text-xs text-[#707A6E] mt-0.5">
                      {new Date(c.completed_at).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Remove this completion of "${c.chore_title}"?`)) return;
                      await api.delete(`/chores/completions/${c.id}`);
                      load();
                    }}
                    className="text-[#C06E52] hover:bg-[#C06E52]/10 p-2 rounded-lg"
                    aria-label="Remove completion"
                    data-testid={`remove-completion-${c.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <DiagnosticsCard kidId={kidId} />
      {showAddDevice && (
        <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm flex items-center justify-center p-6" data-testid="add-device-modal">
          <form onSubmit={addDevice} className="card-soft p-8 w-full max-w-md fade-in-up">
            <h3 className="font-heading text-2xl font-semibold text-[#2C342A]">Add device</h3>
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Name</label>
                <input
                  data-testid="device-name-input"
                  required
                  value={deviceForm.name}
                  onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Type</label>
                <select
                  data-testid="device-type-select"
                  value={deviceForm.type}
                  onChange={(e) => setDeviceForm({ ...deviceForm, type: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]"
                >
                  <option value="phone">Phone</option>
                  <option value="tablet">Tablet</option>
                  <option value="laptop">Laptop</option>
                  <option value="console">Console</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Identifier (MAC / hostname)</label>
                <input
                  data-testid="device-identifier-input"
                  value={deviceForm.identifier}
                  onChange={(e) => setDeviceForm({ ...deviceForm, identifier: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]"
                />
              </div>
            </div>
            <div className="mt-8 flex gap-3">
              <button type="button" onClick={() => setShowAddDevice(false)} className="flex-1 rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 font-medium" data-testid="device-cancel-btn">
                Cancel
              </button>
              <button type="submit" className="flex-1 rounded-xl bg-[#5C7A4F] text-white px-4 py-3 font-medium hover:bg-[#4B6640]" data-testid="device-submit-btn">
                Add device
              </button>
            </div>
          </form>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm flex items-center justify-center p-6" data-testid="edit-kid-modal">
          <form onSubmit={saveKidEdits} className="card-soft p-8 w-full max-w-md fade-in-up">
            <h3 className="font-heading text-2xl font-semibold text-[#2C342A]">Edit {kid.name}</h3>
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Name</label>
                <input
                  data-testid="edit-kid-name-input"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]"
                />
              </div>
              <div className="rounded-xl border border-[#E4E2DC] bg-[#FAFAF7] px-3 py-2 text-xs text-[#707A6E]" data-testid="edit-hours-hint">
                To change hours per day, scroll down to <span className="font-medium">Daily schedule</span> and edit each day individually.
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Reset day</label>
                <select
                  data-testid="edit-kid-resetday-select"
                  value={editForm.reset_weekday}
                  onChange={(e) => setEditForm({ ...editForm, reset_weekday: parseInt(e.target.value) })}
                  className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]"
                >
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">
                  4-digit PIN {kid.has_pin && <span className="text-emerald-600 normal-case tracking-normal ml-1">(set — leave blank to keep)</span>}
                </label>
                <input
                  data-testid="edit-kid-pin-input"
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  maxLength={4}
                  placeholder={kid.has_pin ? "••••" : "e.g. 2413"}
                  value={editForm.pin || ""}
                  onChange={(e) => setEditForm({ ...editForm, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F] font-mono tracking-widest"
                />
                <p className="text-xs text-[#707A6E] mt-1.5">Used for shared iPads — kid enters this in the Yoda Watch app to bill their weekly pool.</p>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">
                  Kid web username {kid.username && <span className="text-emerald-600 normal-case tracking-normal ml-1">(set)</span>}
                </label>
                <input
                  data-testid="edit-kid-username-input"
                  value={editForm.username || ""}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                  placeholder="e.g. bodhi"
                  className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]"
                />
                <p className="text-xs text-[#707A6E] mt-1.5">Kid signs in at <code>/kid/login</code> with this username + their PIN.</p>
              </div>
            </div>
            <div className="mt-8 flex gap-3">
              <button type="button" onClick={() => setEditing(false)} className="flex-1 rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 font-medium" data-testid="edit-kid-cancel-btn">
                Cancel
              </button>
              <button type="submit" className="flex-1 rounded-xl bg-[#5C7A4F] text-white px-4 py-3 font-medium hover:bg-[#4B6640]" data-testid="edit-kid-submit-btn">
                Save changes
              </button>
            </div>
          </form>
        </div>
      )}

      {pairing && (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" data-testid="pair-modal">
          <div className="card-soft p-8 w-full max-w-md fade-in-up text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5C7A4F]/10 text-[#5C7A4F] mb-4">
              {pairing.kind === "windows" ? <MonitorSmartphone size={28} /> : <ShieldCheck size={28} />}
            </div>
            <h3 className="font-heading text-2xl font-semibold text-[#2C342A]">
              {pairing.kind === "windows" ? "Pair Windows PC" : "Pair iOS device"}
            </h3>
            <p className="text-[#707A6E] text-sm mt-2">
              {pairing.kind === "windows"
                ? <>On the kid's PC, signed in as <b>you</b> (an administrator), download and open <b>YodaWatchSetup.exe</b>, then type this code:</>
                : <>On the kid's iPhone/iPad, open the <b>Yoda Watch</b> app and enter this code.</>}
            </p>
            <div
              data-testid="pair-code"
              className="mt-6 font-mono text-5xl tracking-[0.4em] text-[#2C342A] bg-[#F0EFEB] rounded-2xl py-6"
            >
              {pairing.pairing_code}
            </div>
            {pairing.kind === "windows" && (
              <div className="mt-4 rounded-xl bg-[#F5F4F0] border border-[#E4E2DC] p-4 text-left text-xs text-[#2C342A] space-y-2"
                   data-testid="pair-windows-steps">
                <a className="inline-flex items-center gap-2 rounded-lg bg-[#5C7A4F] px-3 py-2 text-sm font-medium text-white hover:bg-[#4B6640]"
                   href="/api/windows-agent/YodaWatchSetup.exe" data-testid="pair-windows-download">
                  Download YodaWatchSetup.exe
                </a>
                <ol className="list-decimal pl-4 space-y-1 text-[#2C342A]">
                  <li>If Windows says it protected your PC, press <b>More info</b>, then <b>Run anyway</b>.</li>
                  <li>Type the code, and pick the Windows account {kid.name || "your kid"} signs in with.</li>
                  <li>Give {kid.name || "your kid"} a <b>standard</b> account (not administrator), so Yoda Watch can't be removed.</li>
                </ol>
                <p className="text-[#707A6E]">Time on the PC comes out of the same daily allowance as the iPhone and iPad. When it runs out the PC locks; their apps stay open behind the lock.</p>
              </div>
            )}
            <p className="text-xs text-[#707A6E] mt-3">
              Code expires{" "}
              {new Date(pairing.expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              . Once paired, the device appears in your devices list.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => { setPairing(null); load(); }}
                className="flex-1 rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 font-medium"
                data-testid="pair-done-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {timeMode && (
        <TimeSheet kid={kid} initialMode={timeMode} onClose={() => setTimeMode(null)}
                   onDone={(m) => { toast.success(m); load(); }} />
      )}
      {messageOpen && (
        <MessageSheet kids={[kid, ...siblings]} initialKidIds={[kid.id]} onClose={() => setMessageOpen(false)}
                      onDone={(m) => { toast.success(m); setTimeout(load, 65000); }} />
      )}
      {tokenOpen && (
        <KidQuickActionsModal kid={kid} onClose={() => setTokenOpen(false)} onChanged={() => load()} />
      )}
    </div>
  );
}

/** The app's time ring: what's left of today's time, with the time in the middle. */
function TimeRing({ left, total, colour }) {
  const r = 56, len = 2 * Math.PI * r;
  const frac = total > 0 ? Math.max(0, Math.min(1, left / total)) : 0;
  return (
    <div className="relative h-[132px] w-[132px] flex-none">
      <svg viewBox="0 0 132 132" width="132" height="132">
        <circle cx="66" cy="66" r={r} fill="none" stroke="#EEECE6" strokeWidth="14" />
        <circle cx="66" cy="66" r={r} fill="none" stroke={colour} strokeWidth="14" strokeLinecap="round"
                strokeDasharray={`${frac * len} ${len}`} transform="rotate(-90 66 66)" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="tabular text-[23px] font-bold tracking-tight text-[#2C342A]">{hm(left)}</div>
        <div className="text-[12.5px] text-[#707A6E]">left today</div>
      </div>
    </div>
  );
}

function ActionTile({ icon, tint, iconColour, title, detail, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="flex flex-col gap-2 rounded-[20px] border border-[#E4E2DC] bg-white p-3.5 text-left hover:bg-[#FAFAF7]">
      <span className="flex h-[38px] w-[38px] items-center justify-center rounded-xl text-lg font-bold"
            style={{ background: tint, color: iconColour }}>{icon}</span>
      <span className="text-[15px] font-semibold text-[#2C342A]">{title}</span>
      <span className="-mt-1 text-xs text-[#707A6E]">{detail}</span>
    </button>
  );
}

function DailyScheduleEditor({ kidId, kid, siblings = [], onSaved }) {
  const [dailyHours, setDailyHours] = useState(kid.daily_hours || {});
  const [dailyWindows, setDailyWindows] = useState(kid.daily_windows || {});
  const [holidayHours, setHolidayHours] = useState(kid.holiday_hours ?? 5);
  const [holidayWindow, setHolidayWindow] = useState(kid.holiday_window || { start: "09:00", end: "20:00" });
  const [rollover, setRollover] = useState(!!kid.rollover_enabled);
  const [state, setState] = useState(kid.state || "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [showHolForm, setShowHolForm] = useState(false);
  const [holForm, setHolForm] = useState({ date: "", end_date: "", name: "School holiday", type: "school" });
  // Track unsaved edits so the parent's 15s auto-refresh cannot wipe them.
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    // Only sync from server when user has no unsaved edits.
    if (dirty) return;
    setDailyHours(kid.daily_hours || {});
    setDailyWindows(kid.daily_windows || {});
    setHolidayHours(kid.holiday_hours ?? 5);
    setHolidayWindow(kid.holiday_window || { start: "09:00", end: "20:00" });
    setRollover(!!kid.rollover_enabled);
    setState(kid.state || "");
  }, [kid, dirty]);

  const loadHolidays = useCallback(async () => {
    if (!state) { setHolidays([]); return; }
    const y = new Date().getFullYear();
    const { data } = await api.get(`/holidays?state=${state}&year=${y}`);
    setHolidays(data);
  }, [state]);
  useEffect(() => { loadHolidays(); }, [loadHolidays]);

  const totalHours = Object.values(dailyHours).reduce((a, b) => a + (parseFloat(b) || 0), 0);

  const setDayHours = (d, v) => {
    setDirty(true);
    setDailyHours((h) => ({ ...h, [d]: Math.max(0, Math.min(24, parseFloat(v) || 0)) }));
  };
  const setDayWindow = (d, k, v) => {
    setDirty(true);
    setDailyWindows((w) => ({ ...w, [d]: { ...(w[d] || {}), [k]: v } }));
  };

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put(`/kids/${kidId}/schedule-config`, {
        daily_hours: dailyHours, daily_windows: dailyWindows,
        holiday_hours: holidayHours, holiday_window: holidayWindow,
        rollover_enabled: rollover, state: state || null,
      });
      setMsg("Saved");
      setDirty(false);
      onSaved?.();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 2000);
    }
  };

  const refreshHolidays = async () => {
    if (!state) return;
    await api.post(`/holidays/refresh?state=${state}`);
    await loadHolidays();
  };
  const addHoliday = async (e) => {
    e.preventDefault();
    if (!state) return;
    await api.post("/holidays", { ...holForm, state });
    setShowHolForm(false);
    setHolForm({ date: "", end_date: "", name: "School holiday", type: "school" });
    loadHolidays();
  };
  const deleteHoliday = async (id) => { await api.delete(`/holidays/${id}`); loadHolidays(); };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#707A6E] mb-1">Daily schedule</div>
          <h3 className="font-heading text-2xl font-medium text-[#2C342A]">Hours & windows per day</h3>
          <p className="text-[#707A6E] text-sm mt-1">Set how many hours + when they can be used. Total this week: <b>{totalHours.toFixed(1)}h</b></p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          {siblings.length > 0 && (
            <select data-testid="copy-from-select" defaultValue=""
                    onChange={async (e) => {
                      const src = e.target.value; if (!src) return;
                      if (!window.confirm("Copy schedule from that kid? This overwrites the current schedule.")) { e.target.value = ""; return; }
                      await api.post(`/kids/${kidId}/copy-schedule-from/${src}`);
                      // Copy-from writes server-side; clear dirty so the useEffect
                      // syncs the freshly copied schedule back into the editor.
                      setDirty(false);
                      onSaved?.();
                      e.target.value = "";
                    }}
                    className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2 text-sm">
              <option value="">Copy from…</option>
              {siblings.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <label className="text-sm text-[#707A6E]">State</label>
          <select value={state} onChange={(e) => { setDirty(true); setState(e.target.value); }} data-testid="kid-state-select"
                  className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2 text-sm">
            <option value="">— none —</option>
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="text-sm text-[#707A6E] inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={rollover} onChange={(e) => { setDirty(true); setRollover(e.target.checked); }} data-testid="rollover-toggle" />
            Roll unused hours into a weekly bank
          </label>
          <button onClick={save} disabled={saving} data-testid="save-schedule-config-btn"
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 font-medium disabled:opacity-50 ${dirty ? "bg-[#D99A2B] text-white hover:bg-[#A87414]" : "bg-[#5C7A4F] text-white hover:bg-[#4B6640]"}`}>
            <Save size={14} /> {saving ? "Saving…" : (dirty ? "Save changes*" : "Save")}
          </button>
          {msg && <span className="text-xs text-[#707A6E]">{msg}</span>}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-xs text-[#707A6E]">
            <tr>
              <th className="text-left font-semibold uppercase tracking-[0.14em] pb-2">Day</th>
              <th className="text-left font-semibold uppercase tracking-[0.14em] pb-2">Hours allowed</th>
              <th className="text-left font-semibold uppercase tracking-[0.14em] pb-2">Available window</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3, 4, 5, 6].map((d) => (
              <tr key={d} className="border-t border-[#E4E2DC]" data-testid={`day-row-${d}`}>
                <td className="py-3 pr-4 text-[#2C342A] font-medium w-20">{DAYS[d]}</td>
                <td className="py-3 pr-4">
                  <input type="number" min="0" max="24" step="0.25" value={dailyHours[d] ?? dailyHours[String(d)] ?? 0}
                         onChange={(e) => setDayHours(d, e.target.value)}
                         data-testid={`day-hours-${d}`}
                         className="w-24 rounded-lg border border-[#E4E2DC] bg-white px-3 py-1.5" />
                  <span className="text-[#707A6E] text-xs ml-1">h</span>
                </td>
                <td className="py-3 pr-4">
                  <input type="time" value={(dailyWindows[d] || dailyWindows[String(d)] || {}).start || "00:00"}
                         onChange={(e) => setDayWindow(d, "start", e.target.value)}
                         data-testid={`day-start-${d}`}
                         className="rounded-lg border border-[#E4E2DC] bg-white px-3 py-1.5" />
                  <span className="text-[#707A6E] text-xs mx-2">to</span>
                  <input type="time" value={(dailyWindows[d] || dailyWindows[String(d)] || {}).end || "23:59"}
                         onChange={(e) => setDayWindow(d, "end", e.target.value)}
                         data-testid={`day-end-${d}`}
                         className="rounded-lg border border-[#E4E2DC] bg-white px-3 py-1.5" />
                </td>
              </tr>
            ))}
            <tr className="border-t border-[#E4E2DC] bg-[#F0EFEB]/60" data-testid="holiday-row">
              <td className="py-3 pr-4 text-[#D99A2B] font-medium w-20 flex items-center gap-1"><PartyPopper size={14} /> Hol.</td>
              <td className="py-3 pr-4">
                <input type="number" min="0" max="24" step="0.25" value={holidayHours}
                       onChange={(e) => { setDirty(true); setHolidayHours(Math.max(0, Math.min(24, parseFloat(e.target.value) || 0))); }}
                       data-testid="holiday-hours"
                       className="w-24 rounded-lg border border-[#E4E2DC] bg-white px-3 py-1.5" />
                <span className="text-[#707A6E] text-xs ml-1">h (school + public hol.)</span>
              </td>
              <td className="py-3 pr-4">
                <input type="time" value={holidayWindow.start} onChange={(e) => { setDirty(true); setHolidayWindow({ ...holidayWindow, start: e.target.value }); }}
                       data-testid="holiday-start"
                       className="rounded-lg border border-[#E4E2DC] bg-white px-3 py-1.5" />
                <span className="text-[#707A6E] text-xs mx-2">to</span>
                <input type="time" value={holidayWindow.end} onChange={(e) => { setDirty(true); setHolidayWindow({ ...holidayWindow, end: e.target.value }); }}
                       data-testid="holiday-end"
                       className="rounded-lg border border-[#E4E2DC] bg-white px-3 py-1.5" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {state && (
        <div className="mt-6 border-t border-[#E4E2DC] pt-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <CalendarClock size={16} className="text-[#D99A2B]" />
              <h4 className="font-heading text-lg font-medium text-[#2C342A]">Holidays ({state})</h4>
              <span className="text-xs text-[#707A6E]">{holidays.length} dates</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowHolForm(true)} data-testid="holiday-add-btn"
                      className="inline-flex items-center gap-1 rounded-xl bg-[#5C7A4F] text-white px-3 py-1.5 text-sm hover:bg-[#4B6640]">
                <Plus size={14} /> Add school break
              </button>
              <button onClick={refreshHolidays} data-testid="holiday-refresh-btn"
                      className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-1.5 text-sm hover:bg-[#F0EFEB]">
                Refresh public
              </button>
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto text-sm space-y-1">
            {holidays.map((h) => (
              <div key={h.id} className="flex items-center justify-between py-1.5 px-2 hover:bg-[#F0EFEB]/60 rounded-lg" data-testid={`holiday-${h.id}`}>
                <span>
                  <span className={`inline-block h-2 w-2 rounded-full mr-2 ${h.type === "public" ? "bg-[#C06E52]" : "bg-[#D99A2B]"}`} />
                  <span className="font-mono text-xs text-[#707A6E]">{h.date}</span>
                  <span className="ml-2 text-[#2C342A]">{h.name}</span>
                  <span className="ml-2 text-xs text-[#707A6E]">({h.type})</span>
                </span>
                {(h.source === "manual" || h.source === "seed") && (
                  <button onClick={() => deleteHoliday(h.id)} className="text-[#C06E52] p-1 hover:bg-[#C06E52]/10 rounded" data-testid={`holiday-del-${h.id}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
            {holidays.length === 0 && <p className="text-[#707A6E] text-xs">No holidays cached. Click Refresh public.</p>}
          </div>

          {showHolForm && (
            <form onSubmit={addHoliday} className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2 bg-[#F0EFEB]/60 p-3 rounded-xl" data-testid="holiday-add-form">
              <input required type="date" value={holForm.date} onChange={(e) => setHolForm({ ...holForm, date: e.target.value })}
                     data-testid="holiday-date" className="rounded-lg border border-[#E4E2DC] bg-white px-3 py-2 text-sm" />
              <input type="date" placeholder="End (opt.)" value={holForm.end_date} onChange={(e) => setHolForm({ ...holForm, end_date: e.target.value })}
                     data-testid="holiday-end-date" className="rounded-lg border border-[#E4E2DC] bg-white px-3 py-2 text-sm" />
              <input required value={holForm.name} onChange={(e) => setHolForm({ ...holForm, name: e.target.value })}
                     data-testid="holiday-name" className="rounded-lg border border-[#E4E2DC] bg-white px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button type="submit" className="flex-1 rounded-lg bg-[#5C7A4F] text-white text-sm hover:bg-[#4B6640]" data-testid="holiday-save-btn">Save</button>
                <button type="button" onClick={() => setShowHolForm(false)} className="rounded-lg border border-[#E4E2DC] bg-white text-sm px-3">×</button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Diagnostics — helps parents/dev debug "usage isn't accruing" issues.
 */
function DiagnosticsCard({ kidId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setError("");
    try {
      const { data } = await api.get(`/kids/${kidId}/debug`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    }
  }

  useEffect(() => { if (open) load(); }, [open, kidId]);

  function fmtAgo(iso) {
    if (!iso) return "never";
    const secs = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
    return `${Math.round(secs / 86400)}d ago`;
  }

  return (
    <div className="card-soft p-6 mt-6" data-testid="kid-diagnostics">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#707A6E]">Diagnostics</div>
          <h3 className="font-heading text-2xl font-medium text-[#2C342A]">Why isn't time accruing?</h3>
        </div>
        <button data-testid="diagnostics-toggle"
                onClick={() => setOpen(!open)}
                className="text-sm font-medium text-[#5C7A4F] hover:underline">
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-4 text-sm">
          {error && <div className="rounded-xl bg-[#F6E3DC] p-3 text-[#A35A42]" data-testid="diagnostics-error">{error}</div>}
          {!data && !error && <div className="text-[#707A6E]">Loading…</div>}
          {data && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <DiagStat label="Last event received" value={fmtAgo(data.last_activity_event_at)} testid="diag-last-event-ago" />
                <DiagStat label="Last event type" value={data.last_activity_event || "—"} testid="diag-last-event-type" />
                <DiagStat label="Used today (backend)" value={`${Math.round((data.used_today_seconds || 0) / 60)} min`} testid="diag-used-today" />
                <DiagStat label="Apps-to-shield configured" value={data.ios_selection_configured ? "Yes" : "No — pick apps on the iPad"}
                      good={data.ios_selection_configured} testid="diag-selection" />
                <DiagStat label="Paused" value={data.paused ? "Yes" : "No"} testid="diag-paused" />
                <DiagStat label="iOS devices paired" value={data.devices?.length || 0} testid="diag-device-count" />
                <DiagStat label="Extension heartbeat"
                      value={fmtAgo(data.last_extension_heartbeat_at)}
                      good={data.last_extension_heartbeat_at ? true : undefined}
                      testid="diag-extension-heartbeat" />
              </div>

              {data.devices && data.devices.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E] mb-2">Paired devices</div>
                  <div className="space-y-2">
                    {data.devices.map((d) => (
                      <div key={d.device_id} className="rounded-lg border border-[#F0EFEB] p-3" data-testid={`diag-device-${d.device_id}`}>
                        <div className="font-medium">{d.name}</div>
                        <div className="text-xs text-[#707A6E] mt-0.5">
                          {d.model} · iOS {d.ios_version || "?"} · APNs {d.apns_token_present ? "✅" : "❌"} · last seen {fmtAgo(d.last_seen_at)}
                        </div>
                        {/* Schedule-registration outcome — the smoking gun for
                            "extension is silent" diagnosis. Renders only if the
                            device has ever reported a schedule attempt. */}
                        {d.last_schedule_attempt_at && (
                          <div className="mt-2 rounded border border-[#F0EFEB] bg-[#FAFAF7] px-2 py-1.5 text-xs"
                               data-testid={`diag-schedule-${d.device_id}`}>
                            <div className="flex items-center justify-between">
                              <span className="font-semibold uppercase tracking-wider text-[#707A6E]">
                                Schedule
                              </span>
                              <span className={d.last_schedule_success ? "text-[#5C7A4F]" : "text-[#C06E52]"}>
                                {d.last_schedule_success ? "OK" : "FAIL"} · {fmtAgo(d.last_schedule_attempt_at)}
                              </span>
                            </div>
                            {!d.last_schedule_success && d.last_schedule_error && (
                              <div className="mt-1 text-[#C06E52] font-mono break-words" data-testid={`diag-schedule-error-${d.device_id}`}>
                                {d.last_schedule_error}
                              </div>
                            )}
                            <div className="mt-1 text-[#707A6E]">
                              auth: <span className="font-mono">{d.last_schedule_auth_status || "?"}</span>
                              {" · "}apps: <span className="font-mono">{d.last_schedule_apps ?? 0}</span>
                              {" · "}cats: <span className="font-mono">{d.last_schedule_categories ?? 0}</span>
                              {" · "}events: <span className="font-mono">{d.last_schedule_events ?? 0}</span>
                            </div>
                            {/* Stale-selection warning: backend says a selection blob IS
                                configured, but this device decoded 0 apps / 0 cats / 0 domains.
                                Happens after every Xcode reinstall — Apple invalidates the
                                opaque FamilyActivitySelection tokens on fresh install. */}
                            {d.last_schedule_success
                              && data.ios_selection_configured
                              && (d.last_schedule_apps ?? 0) === 0
                              && (d.last_schedule_categories ?? 0) === 0
                              && (d.last_schedule_web_domains ?? 0) === 0 && (
                              <div className="mt-2 rounded bg-[#F6E3DC] px-2 py-1.5 text-[#A35A42]"
                                   data-testid={`diag-stale-selection-${d.device_id}`}>
                                ⚠️ Selection stale on this device — parent needs to re-pick
                                "Apps to shield" on this iPad. Apple invalidates tokens after
                                each app reinstall (normal behaviour, dev-only).
                              </div>
                            )}
                          </div>
                        )}
                        {!d.last_schedule_attempt_at && (
                          <div className="mt-2 text-xs text-[#C06E52]" data-testid={`diag-schedule-missing-${d.device_id}`}>
                            No schedule attempt reported yet — open the Yoda Watch app on this device once.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E] mb-2">
                  Recent events (last 20)
                </div>
                {data.recent_events && data.recent_events.length > 0 ? (
                  <div className="rounded-lg border border-[#F0EFEB] overflow-hidden" data-testid="diag-events-list">
                    {data.recent_events.map((ev, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 px-3 py-2 text-xs even:bg-[#FAFAF7]">
                        <div className="min-w-0">
                          <div className="font-mono text-[#2C342A]">{ev.event}</div>
                          {ev.device_name && <div className="text-[#707A6E] truncate">{ev.device_name}</div>}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[#707A6E]">
                            {Math.round((ev.used_seconds || 0) / 60)}m today
                            {ev.added_seconds != null && <> · +{Math.round(ev.added_seconds / 60)}m</>}
                          </div>
                          <div className="text-[#A6ABA3]" title={ev.source || ""}>{fmtAgo(ev.at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[#707A6E] italic" data-testid="diag-events-empty">
                    No events received yet from the DeviceActivityMonitor extension.
                    <br />
                    Common causes: extension not installed, "Apps to shield" not picked, or kid disabled Yoda Watch in Screen Time settings.
                  </div>
                )}
              </div>

              <button data-testid="diagnostics-refresh"
                      onClick={load}
                      className="rounded-lg border border-[#E4E2DC] px-3 py-1.5 text-xs font-medium">
                Refresh
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DiagStat({ label, value, good, testid }) {
  return (
    <div className="rounded-lg border border-[#F0EFEB] p-3" data-testid={testid}>
      <div className="text-xs text-[#707A6E]">{label}</div>
      <div className={`font-medium mt-0.5 ${good === false ? "text-[#C06E52]" : good === true ? "text-[#5C7A4F]" : "text-[#2C342A]"}`}>
        {value}
      </div>
    </div>
  );
}


